import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { evaluateFormula, formatResult } from '../../../utils/calcFieldUtils';

const FORMAT_OPTIONS = [
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'text', label: 'Plain text' },
];

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CAD', label: 'CAD (C$)' },
  { value: 'AUD', label: 'AUD (A$)' },
];

const DECIMAL_OPTIONS = [
  { value: '0', label: '0 decimals' },
  { value: '1', label: '1 decimal' },
  { value: '2', label: '2 decimals' },
  { value: '3', label: '3 decimals' },
];

const emptyCalcField = () => ({
  id: `calc_${Date.now()}`,
  label: 'Calculated Field',
  name: `calc_field_${Date.now()}`,
  formula: '',
  format: 'number',
  currency: 'USD',
  decimals: '2',
  prefix: '',
  suffix: '',
  showInForm: true,
  enabled: true,
  products: [],
});

// ── AddProductForm ────────────────────────────────────────────────────────────
const AddProductForm = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [billingType, setBillingType] = useState('one_off');

  const handleAdd = () => {
    if (!name?.trim() || price === '') return;
    onAdd({
      id: `prod_${Date.now()}_${Math.random()?.toString(36)?.slice(2, 6)}`,
      name: name?.trim(),
      price: parseFloat(price) || 0,
      billingType,
    });
    setName('');
    setPrice('');
    setBillingType('one_off');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full py-1.5 text-xs text-primary border border-dashed border-primary/40 rounded-md hover:bg-primary/5 transition-colors flex items-center justify-center gap-1.5"
      >
        <Icon name="Plus" size={12} />
        Add product / service
      </button>
    );
  }

  return (
    <div className="p-2.5 border border-primary/30 rounded-md bg-primary/5 space-y-2">
      <input
        type="text"
        value={name}
        onChange={e => setName(e?.target?.value)}
        placeholder="Product name (e.g. Consulting)"
        className="w-full px-2.5 py-1.5 text-xs bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
          <input
            type="number"
            value={price}
            onChange={e => setPrice(e?.target?.value)}
            placeholder="0.00"
            min="0"
            step="0.01"
            className="w-full pl-6 pr-2.5 py-1.5 text-xs bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex rounded-md border border-input overflow-hidden text-[10px] font-medium flex-shrink-0">
          {[{ val: 'one_off', label: 'One-off' }, { val: 'monthly', label: '/mo' }]?.map(opt => (
            <button
              key={opt?.val}
              type="button"
              onClick={() => setBillingType(opt?.val)}
              className={`px-2 py-1.5 transition-colors ${billingType === opt?.val ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
            >
              {opt?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleAdd}
          disabled={!name?.trim() || price === ''}
          className="flex-1 py-1.5 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setName(''); setPrice(''); setBillingType('one_off'); }}
          className="px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted transition-colors text-muted-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Step heading ──────────────────────────────────────────────────────────────
const StepHeading = ({ number, title, subtitle }) => (
  <div className="flex items-start gap-2.5 mb-2">
    <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
      {number}
    </span>
    <div>
      <h4 className="text-xs font-semibold text-foreground">{title}</h4>
      {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// ── Main panel ────────────────────────────────────────────────────────────────
const CalculatedFieldsPanel = ({ formElements = [], calcFields = [], onUpdateCalcFields }) => {
  const [expandedField, setExpandedField] = useState(null);
  const [previewValues, setPreviewValues] = useState({});
  // editingProduct: { calcId, productId, name, price, billingType } | null
  const [editingProduct, setEditingProduct] = useState(null);

  const numericFields = formElements?.filter(el =>
    el?.type === 'input' || el?.type === 'select' || el?.type === 'radio' || el?.type === 'number'
  );

  const addCalcField = () => {
    const newField = emptyCalcField();
    onUpdateCalcFields?.([...calcFields, newField]);
    setExpandedField(newField?.id);
  };

  const removeCalcField = (id) => {
    onUpdateCalcFields?.(calcFields?.filter(f => f?.id !== id));
    if (expandedField === id) setExpandedField(null);
  };

  const updateCalcField = (id, changes) => {
    onUpdateCalcFields?.(calcFields?.map(f => f?.id === id ? { ...f, ...changes } : f));
  };

  const startEditProduct = (calcId, product) => {
    setEditingProduct({
      calcId,
      productId: product?.id,
      name: product?.name || '',
      price: String(product?.price ?? ''),
      billingType: product?.billingType || 'one_off',
    });
  };

  const saveEditProduct = () => {
    if (!editingProduct) return;
    const { calcId, productId, name, price, billingType } = editingProduct;
    const cf = calcFields?.find(f => f?.id === calcId);
    if (!cf) return;
    const oldProduct = (cf?.products || [])?.find(p => p?.id === productId);
    const oldName = oldProduct?.name;
    const updatedProducts = (cf?.products || [])?.map(p =>
      p?.id === productId ? { ...p, name: name?.trim(), price: parseFloat(price) || 0, billingType } : p
    );
    // If name changed, update formula token references automatically
    let updatedFormula = cf?.formula || '';
    if (oldName && name?.trim() && oldName !== name?.trim()) {
      const safeOld = oldName?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      updatedFormula = updatedFormula
        ?.replace(new RegExp(`\\{${safeOld}\\}`, 'g'), `{${name?.trim()}}`)
        ?.replace(new RegExp(`\\b${safeOld}\\b`, 'g'), name?.trim());
    }
    updateCalcField(calcId, { products: updatedProducts, formula: updatedFormula });
    setEditingProduct(null);
  };

  const insertToken = (token, calcId) => {
    const cf = calcFields?.find(f => f?.id === calcId);
    if (!cf) return;
    updateCalcField(calcId, { formula: (cf?.formula || '') + token });
  };

  const getLiveResult = (cf) => {
    const raw = evaluateFormula(cf?.formula, previewValues, cf?.products || []);
    if (raw === '' || raw === 'Error' || raw === 'Invalid formula') return raw || '—';
    return formatResult(raw, cf?.format, cf?.currency, cf?.decimals, cf?.prefix, cf?.suffix);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-3">
        <Icon name="Calculator" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-foreground">Calculated Fields</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Define products & services, then build a formula to compute live totals as respondents fill in values.
          </p>
        </div>
      </div>

      {/* Global test-values panel */}
      {numericFields?.length > 0 && (
        <div className="p-3 bg-muted/20 rounded-lg border border-border space-y-2">
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Icon name="FlaskConical" size={12} />
            Test Values (preview only)
          </h4>
          <p className="text-[11px] text-muted-foreground">Enter sample numbers to see a live formula result.</p>
          <div className="grid grid-cols-2 gap-2">
            {numericFields?.slice(0, 4)?.map(f => (
              <div key={f?.id}>
                <label className="text-xs text-muted-foreground block mb-0.5 truncate">{f?.label || f?.name}</label>
                <input
                  type="number"
                  value={previewValues?.[f?.name || f?.id] || ''}
                  onChange={(e) => setPreviewValues(prev => ({ ...prev, [f?.name || f?.id]: e?.target?.value }))}
                  placeholder="0"
                  className="w-full px-2 py-1 text-xs bg-background border border-input rounded focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calc fields list */}
      {calcFields?.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
          <Icon name="Calculator" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No calculated fields yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add one to compute live totals, quotes, or estimates</p>
        </div>
      ) : (
        <div className="space-y-2">
          {calcFields?.map((cf) => {
            const liveResult = getLiveResult(cf);
            const hasError = liveResult === 'Error' || liveResult === 'Invalid formula';
            const isExpanded = expandedField === cf?.id;
            const products = cf?.products || [];

            return (
              <div key={cf?.id} className="border border-border rounded-lg overflow-hidden bg-card">
                {/* Card header */}
                <div
                  className="flex items-center gap-2 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedField(isExpanded ? null : cf?.id)}
                >
                  <Icon name="Calculator" size={14} color="var(--color-primary)" className="flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground flex-1 truncate">{cf?.label || 'Calculated Field'}</span>
                  {cf?.formula && (
                    <span className={`text-xs font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${hasError ? 'bg-destructive/10 text-destructive' : 'bg-green-50 text-green-700'}`}>
                      {liveResult}
                    </span>
                  )}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => { e?.stopPropagation(); updateCalcField(cf?.id, { enabled: !cf?.enabled }); }}
                      className={`p-1 rounded transition-colors ${cf?.enabled ? 'text-green-600 hover:bg-green-50' : 'text-muted-foreground hover:bg-muted'}`}
                      title={cf?.enabled ? 'Disable' : 'Enable'}
                    >
                      <Icon name={cf?.enabled ? 'ToggleRight' : 'ToggleLeft'} size={16} />
                    </button>
                    <button
                      onClick={(e) => { e?.stopPropagation(); removeCalcField(cf?.id); }}
                      className="p-1 rounded text-destructive hover:bg-destructive/10 transition-colors"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} color="var(--color-muted-foreground)" />
                  </div>
                </div>

                {/* Expanded editor */}
                {isExpanded && (
                  <div className="border-t border-border p-3 space-y-4 bg-muted/10">
                    {/* Field identity */}
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        label="Display Label"
                        value={cf?.label}
                        onChange={(e) => updateCalcField(cf?.id, { label: e?.target?.value })}
                        placeholder="Total Price"
                      />
                      <Input
                        label="Field Name"
                        value={cf?.name}
                        onChange={(e) => updateCalcField(cf?.id, { name: e?.target?.value })}
                        placeholder="total_price"
                      />
                    </div>

                    {/* ── Step 1: Products ─────────────────────────────── */}
                    <div className="p-3 border border-border rounded-lg bg-background space-y-2.5">
                      <StepHeading
                        number="1"
                        title="Products & Services"
                        subtitle={`Define named items with prices. Reference them in your formula using {Name} or just the name.`}
                      />

                      {/* Product list */}
                      {products?.length > 0 && (
                        <div className="space-y-1.5">
                          {products?.map(product => {
                            const isEditingThis = editingProduct?.calcId === cf?.id && editingProduct?.productId === product?.id;
                            if (isEditingThis) {
                              return (
                                <div key={product?.id} className="p-2.5 border border-primary/30 rounded-md bg-primary/5 space-y-2">
                                  <input
                                    type="text"
                                    value={editingProduct?.name}
                                    onChange={e => setEditingProduct(prev => ({ ...prev, name: e?.target?.value }))}
                                    placeholder="Product name"
                                    className="w-full px-2.5 py-1.5 text-xs bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                  />
                                  <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
                                      <input
                                        type="number"
                                        value={editingProduct?.price}
                                        onChange={e => setEditingProduct(prev => ({ ...prev, price: e?.target?.value }))}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                        className="w-full pl-6 pr-2.5 py-1.5 text-xs bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                      />
                                    </div>
                                    <div className="flex rounded-md border border-input overflow-hidden text-[10px] font-medium flex-shrink-0">
                                      {[{ val: 'one_off', label: 'One-off' }, { val: 'monthly', label: '/mo' }]?.map(opt => (
                                        <button
                                          key={opt?.val}
                                          type="button"
                                          onClick={() => setEditingProduct(prev => ({ ...prev, billingType: opt?.val }))}
                                          className={`px-2 py-1.5 transition-colors ${editingProduct?.billingType === opt?.val ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                                        >
                                          {opt?.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={saveEditProduct}
                                      disabled={!editingProduct?.name?.trim() || editingProduct?.price === ''}
                                      className="flex-1 py-1.5 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Save
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingProduct(null)}
                                      className="px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted transition-colors text-muted-foreground"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div key={product?.id} className="flex items-center gap-2 px-2.5 py-2 bg-muted/30 border border-border rounded-md">
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-medium text-foreground block truncate">{product?.name || 'Unnamed'}</span>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[10px] text-green-700 font-mono bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
                                      ${parseFloat(product?.price || 0)?.toFixed(2)}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground">
                                      {product?.billingType === 'monthly' ? '/ month' : 'one-off'}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  title={`Insert {${product?.name}} into formula`}
                                  onClick={() => insertToken(`{${product?.name}}`, cf?.id)}
                                  className="p-1.5 text-primary hover:bg-primary/10 rounded transition-colors flex-shrink-0"
                                >
                                  <Icon name="CurlyBraces" size={12} />
                                </button>
                                <button
                                  type="button"
                                  title="Edit product"
                                  onClick={() => startEditProduct(cf?.id, product)}
                                  className="p-1.5 text-muted-foreground hover:bg-muted rounded transition-colors flex-shrink-0"
                                >
                                  <Icon name="Pencil" size={12} />
                                </button>
                                <button
                                  type="button"
                                  title="Remove product"
                                  onClick={() => updateCalcField(cf?.id, { products: products?.filter(p => p?.id !== product?.id) })}
                                  className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                                >
                                  <Icon name="Trash2" size={12} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <AddProductForm
                        onAdd={(product) => updateCalcField(cf?.id, { products: [...products, product] })}
                      />
                    </div>

                    {/* ── Step 2: Formula ──────────────────────────────── */}
                    <div className="p-3 border border-border rounded-lg bg-background space-y-2.5">
                      <StepHeading
                        number="2"
                        title="Build Your Formula"
                        subtitle="Use {field_name} tokens from your form fields, or reference products by name."
                      />

                      <textarea
                        value={cf?.formula}
                        onChange={(e) => updateCalcField(cf?.id, { formula: e?.target?.value })}
                        placeholder={products?.length > 0
                          ? `e.g. ${products[0]?.name || 'ProductName'} * quantity`
                          : 'e.g. quantity * unit_price * (1 - discount / 100)'}
                        rows={3}
                        className={`w-full px-2.5 py-2 bg-background border rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary resize-none ${hasError && cf?.formula ? 'border-destructive' : 'border-input'}`}
                      />
                      {hasError && cf?.formula && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <Icon name="AlertCircle" size={12} />
                          {liveResult} — check your formula syntax
                        </p>
                      )}

                      {/* Quick-insert: products */}
                      {products?.length > 0 && (
                        <div>
                          <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Insert product:</label>
                          <div className="flex flex-wrap gap-1">
                            {products?.map(p => (
                              <button
                                key={p?.id}
                                type="button"
                                onClick={() => insertToken(`{${p?.name}}`, cf?.id)}
                                className="px-2 py-0.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 transition-colors font-mono"
                                title={`$${parseFloat(p?.price || 0)?.toFixed(2)} — ${p?.billingType === 'monthly' ? '/mo' : 'one-off'}`}
                              >
                                {'{' + p?.name + '}'}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quick-insert: form fields */}
                      {numericFields?.length > 0 && (
                        <div>
                          <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Insert field:</label>
                          <div className="flex flex-wrap gap-1">
                            {numericFields?.map(f => (
                              <button
                                key={f?.id}
                                type="button"
                                onClick={() => insertToken(`{${f?.name || f?.id}}`, cf?.id)}
                                className="px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20 rounded hover:bg-primary/20 transition-colors font-mono"
                              >
                                {'{' + (f?.name || f?.id) + '}'}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Example formulas */}
                      <div className="pt-1 border-t border-border">
                        <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Examples:</label>
                        <div className="space-y-0.5">
                          {[
                            { label: 'Quantity × price', formula: products?.length > 0 ? `${products[0]?.name} * quantity` : 'quantity * price' },
                            { label: 'With discount', formula: 'price * (1 - discount / 100)' },
                            { label: 'Tax added', formula: 'subtotal * 1.10' },
                          ]?.map(ex => (
                            <button
                              key={ex?.label}
                              type="button"
                              onClick={() => updateCalcField(cf?.id, { formula: ex?.formula })}
                              className="w-full text-left px-2 py-1 rounded hover:bg-muted/50 transition-colors group"
                            >
                              <span className="text-xs font-medium text-foreground group-hover:text-primary">{ex?.label}: </span>
                              <span className="text-xs font-mono text-muted-foreground">{ex?.formula}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* ── Step 3: Preview result ───────────────────────── */}
                    <div className="p-3 border border-border rounded-lg bg-background space-y-2.5">
                      <StepHeading
                        number="3"
                        title="Preview Result"
                        subtitle="Live result based on the test values above."
                      />

                      <div className={`px-3 py-2.5 rounded-md border flex items-center gap-2 ${
                        hasError && cf?.formula
                          ? 'bg-destructive/5 border-destructive/30 text-destructive'
                          : 'bg-green-50 border-green-200 text-green-800'
                      }`}>
                        <Icon name={hasError && cf?.formula ? 'AlertCircle' : 'CheckCircle2'} size={14} />
                        <span className="text-sm font-mono font-medium">
                          {cf?.formula ? liveResult : '—'}
                        </span>
                      </div>

                      {/* Output format settings */}
                      <div className="space-y-2 pt-1">
                        <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block">Output Format</label>
                        <div className="grid grid-cols-2 gap-2">
                          <Select
                            label="Format"
                            options={FORMAT_OPTIONS}
                            value={cf?.format}
                            onChange={(val) => updateCalcField(cf?.id, { format: val })}
                          />
                          <Select
                            label="Decimals"
                            options={DECIMAL_OPTIONS}
                            value={cf?.decimals || '2'}
                            onChange={(val) => updateCalcField(cf?.id, { decimals: val })}
                          />
                        </div>
                        {cf?.format === 'currency' && (
                          <Select
                            label="Currency"
                            options={CURRENCY_OPTIONS}
                            value={cf?.currency || 'USD'}
                            onChange={(val) => updateCalcField(cf?.id, { currency: val })}
                          />
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            label="Prefix"
                            value={cf?.prefix || ''}
                            onChange={(e) => updateCalcField(cf?.id, { prefix: e?.target?.value })}
                            placeholder="e.g. ~"
                          />
                          <Input
                            label="Suffix"
                            value={cf?.suffix || ''}
                            onChange={(e) => updateCalcField(cf?.id, { suffix: e?.target?.value })}
                            placeholder="e.g. /mo"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        iconName="Plus"
        iconPosition="left"
        onClick={addCalcField}
        className="w-full"
      >
        Add Calculated Field
      </Button>
      {calcFields?.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {calcFields?.filter(f => f?.enabled)?.length} of {calcFields?.length} field{calcFields?.length !== 1 ? 's' : ''} active
        </p>
      )}
    </div>
  );
};

export default CalculatedFieldsPanel;
